import React, { ReactNode, Suspense, lazy, useState } from 'react';
import { Modal, Spin, Tag, Tooltip, Upload } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { IGalleryCandidate, IGallerySlot } from '../../../types/models/presentations';

// Lazy: the gallery editor brings drag-and-drop tabs and uploads with it, and
// only a click on "Manage galleries" needs them.
const RecordGalleries = lazy(() => import('../../RecordGalleries'));

/**
 * What the two photo screens share: the gallery slots' (`GalleryPicksModal`)
 * and the amenity grid's (`AmenityPhotosModal`). Both are "select a box, then
 * a photo" over the same candidates from the same endpoint; they differ in what
 * a box is and what saving it means.
 *
 * The pool opens with an Upload tile — the development slide's pool too — so a
 * missing photo does not mean closing everything for another page: the screen
 * uploads it to a Brochure gallery (off the website; see `gallery.py` in
 * ancana_api), reloads the pool and drops the first new photo into the
 * selected box.
 */

/** Every slot is drawn this tall, at its own aspect, so shapes compare honestly. */
export const BOX_HEIGHT = 150;

/** A picker's Close and Save, under it — in a modal or embedded alike. */
export const PickerFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

export const PickerBody = styled.div`
  .slide-label {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
    margin-bottom: 6px;
  }

  .box {
    position: relative;
    height: ${BOX_HEIGHT}px;
    background: #f5f5f5;
    border: 2px solid transparent;
    border-radius: 2px;
    overflow: hidden;
    cursor: pointer;
    padding: 0;
  }

  .box.selected {
    border-color: #1890ff;
  }

  .box img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .box .slot-name {
    position: absolute;
    left: 4px;
    bottom: 4px;
    font-size: 11px;
    color: #fff;
    background: rgba(0, 0, 0, 0.55);
    padding: 0 4px;
    border-radius: 2px;
  }

  .box .clear {
    position: absolute;
    top: 4px;
    right: 4px;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    border-radius: 2px;
    padding: 0 3px;
  }

  .pool-label {
    display: flex;
    justify-content: space-between;
    color: rgba(0, 0, 0, 0.65);
    margin-bottom: 8px;
  }

  .pool {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 6px;
  }

  .photo {
    position: relative;
    aspect-ratio: 1;
    border: 2px solid transparent;
    border-radius: 2px;
    overflow: hidden;
    cursor: pointer;
    padding: 0;
    background: #f5f5f5;
  }

  .photo:disabled {
    cursor: default;
    opacity: 0.4;
  }

  .photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .photo .in-slot {
    position: absolute;
    left: 4px;
    bottom: 4px;
  }
`;

/**
 * The slide a slot is shown under. The brochure's numbering wins over other
 * kinds of template, whose slides may be numbered differently.
 */
export const slideOf = (slot: IGallerySlot): number | null => {
  const brochure = slot.boxes.find((box) => box.namespace === 'brochure');
  return (brochure ?? slot.boxes[0])?.slide ?? null;
};

/** A slot's width at `BOX_HEIGHT`, from the first template that knows its shape. */
export const boxWidth = (slot: IGallerySlot) =>
  BOX_HEIGHT * (slot.boxes.find((box) => box.aspect)?.aspect ?? 1);

const Tile = styled.div`
  aspect-ratio: 1;

  .ant-upload.ant-upload-drag {
    height: 100%;
  }

  .ant-upload-btn {
    display: flex !important;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    height: 100%;
    padding: 0 !important;
    color: rgba(0, 0, 0, 0.55);
    font-size: 12px;
  }
`;

/**
 * A pool square to click or drop photos on. Several picked at once arrive as
 * one batch, so they are one request and one reload.
 */
export const UploadTile = ({ uploading, onFiles }: {
  uploading: boolean;
  onFiles: (files: File[]) => void;
}) => (
  <Tile>
    <Upload.Dragger
      accept="image/*"
      multiple
      showUploadList={false}
      disabled={uploading}
      // Nothing uploads on its own: the batch goes when antd hands over its last
      // file, through the screen's endpoint.
      beforeUpload={(file, batch) => {
        if (file === batch[batch.length - 1]) onFiles(batch as File[]);
        return false;
      }}
    >
      {uploading ? <Spin size="small" /> : <PlusOutlined style={{ fontSize: 18 }} />}
      <span>{uploading ? 'Uploading…' : 'Upload'}</span>
    </Upload.Dragger>
  </Tile>
);

/**
 * The full gallery editor for the record uploads go to — rename, reorder, or
 * delete a photo uploaded by mistake. `onClose` reloads the pool.
 */
export const ManageGalleriesLink = ({ recordPath, recordId, recordName, onClose }: {
  recordPath: string;
  recordId: string | number;
  recordName: string;
  onClose: () => void;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <a onClick={() => setOpen(true)}>Manage galleries</a>
      <Modal
        visible={open}
        onCancel={() => { setOpen(false); onClose(); }}
        title={`${recordName}'s Image Galleries`}
        footer={null}
        width="832px"
        destroyOnClose
      >
        <Suspense fallback={<Spin />}>
          <RecordGalleries recordPath={recordPath} recordId={recordId} recordName={recordName} />
        </Suspense>
      </Modal>
    </>
  );
};

/** Picks whose photo is still offered: one deleted in the gallery editor is let go. */
export const keepOffered = <T,>(
  picks: Record<string, T>,
  candidates: { ref: string }[],
  refOf: (pick: T) => string,
): Record<string, T> => {
  const offered = new Set(candidates.map((candidate) => candidate.ref));
  return Object.fromEntries(
    Object.entries(picks).filter(([, pick]) => offered.has(refOf(pick))));
};

/** The photos to choose from, each tagged with the box it already fills. */
export const PhotoPool = ({
  candidates, selected, slotOf, onChoose, onUpload, uploading = false, manage,
}: {
  candidates: IGalleryCandidate[];
  selected?: string;
  /** The box a photo sits in, if any — shown as a tag on it. */
  slotOf: (ref: string) => string | undefined;
  onChoose: (ref: string) => void;
  /** Upload photos; the tile shows only when given. */
  onUpload?: (files: File[]) => void;
  uploading?: boolean;
  /** Beside the label: the link to the gallery uploads go to. */
  manage?: ReactNode;
}) => (
  <>
    <div className="pool-label">
      <span>
        {selected
          ? `Choose a photo for ${selected}`
          : 'Select a slot above, then choose its photo'}
      </span>
      {manage}
    </div>
    <div className="pool">
      {onUpload && <UploadTile uploading={uploading} onFiles={onUpload} />}
      {candidates.map((candidate) => {
        const inSlot = slotOf(candidate.ref);
        return (
          <Tooltip
            key={candidate.ref}
            title={[candidate.source, candidate.gallery, candidate.name]
              .filter(Boolean).join(' · ')}
          >
            <button
              type="button"
              className="photo"
              aria-label={candidate.ref}
              disabled={!selected}
              onClick={() => onChoose(candidate.ref)}
            >
              <img src={candidate.thumbnail} alt={candidate.name} />
              {inSlot && <Tag className="in-slot" color="blue">{inSlot}</Tag>}
            </button>
          </Tooltip>
        );
      })}
    </div>
  </>
);
